import { useCallback, useEffect, useRef } from "react";

import { isBrowser } from "@/common/utils/general.utils";

const STORAGE_SAVE_EVENT = "useStorage:save";

export type StorageSaveDetail = {
  type: "local" | "session";
  key: string;
  data: unknown;
};

export const useStorage = (
  type: "local" | "session",
  onSave?: (detail: StorageSaveDetail) => void
) => {
  const source = type === "local" ? "localStorage" : "sessionStorage";

  /**
   * Retrieves and parses a value from a specified source in the browser's storage.
   *
   * This is only executed in a browser environment. It attempts to retrieve an item by its key
   * from the specified storage source (e.g., localStorage or sessionStorage), parses the retrieved data as JSON,
   * and returns it as the specified type.
   *
   * Will return `undefined` if the environment is not a browser, if the key is not found in the
   * storage, if the storage source is undefined, if storage
   * throws, or if the stored value is not valid JSON.
   *
   * @template T The type of the parsed data being returned.
   * @param {string} key The key of the item to retrieve from the storage.
   *
   * @returns {(T | undefined)} The parsed data as the specified type, or `undefined` if retrieval or parsing fails.
   */
  const load = useCallback(
    <T>(key: string) => {
      if (!isBrowser()) {
        return;
      }

      // Storage can throw (blocked site data, private mode) and a stored
      // value can be corrupt: both read as "nothing stored".
      try {
        const data = window[source].getItem(key);

        if (!data) {
          return;
        }

        return JSON.parse(data) as T;
      } catch {
        return;
      }
    },
    [source]
  );

  /**
   * Saves data to a specified source in the browser's storage.
   *
   * Will not perform any action if the runtime environment is not a browser.
   *
   * @template T
   * @param {string} key - The key under which the data will be saved in the storage.
   * @param {T} data - The data to be saved, which will be serialized to a JSON string.
   */
  const save = useCallback(
    <T>(key: string, data: T) => {
      if (!isBrowser()) {
        return;
      }

      // Full or blocked storage: the value is simply not kept.
      try {
        window[source].setItem(key, JSON.stringify(data));
      } catch {
        return;
      }

      window.dispatchEvent(
        new CustomEvent<StorageSaveDetail>(STORAGE_SAVE_EVENT, {
          detail: { type, key, data },
        })
      );
    },
    [source, type]
  );

  /**
   * Held in a ref so callers can pass inline callbacks without `useCallback` —
   * the listener stays bound once instead of re-binding on every render.
   */
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  useEffect(() => {
    const { current: onSave } = onSaveRef;

    const handleStorageSave = (event: Event) => {
      onSave?.((event as CustomEvent<StorageSaveDetail>).detail);
    };

    window.addEventListener(STORAGE_SAVE_EVENT, handleStorageSave);

    /** Unmount - cleanup. */
    return () => {
      window.removeEventListener(STORAGE_SAVE_EVENT, handleStorageSave);
    };
  }, []);

  return { load, save };
};
